<template>
  <b-container class="my-4" fluid="lg">
    <!-- User Interface controls -->
    <div>
      <b-row>
        <b-col>
          <div>
            <label class="bold">Template</label>
            <b-form-select
                id="template-select"
                ref="templateSelect"
                v-model="selectedTemplate"
                @change="changeTemplate()">
              <b-form-select-option
                  v-for="template in templates"
                  :key="template.name"
                  :value="template">
                {{ template.name }}
              </b-form-select-option>
            </b-form-select>
          </div>

          <div class="mt-2">
            <b-button variant="outline-primary" @click="showTemplateModal">New</b-button>
            <b-button
                :disabled="templateDeleteDisabled"
                variant="outline-danger"
                @click="deleteTemplate">Delete
            </b-button>
          </div>

          <b-modal
              id="template-modal"
              ref="templateModal"
              centered
              hide-footer
              title="Add Template"
              @bv::modal::hidden="this.errors.template = []">
            <b-form id="template-form" @submit.prevent="submitTemplateForm">
              <b-input-group class="mt-2" prepend="Name">
                <b-form-input
                    id="template-name-input"
                    ref="templateNameInput"
                    v-model="templateModal.name">
                </b-form-input>
              </b-input-group>

              <b-input-group
                  id="interval-input-group"
                  class="mt-2"
                  prepend="Days Interval">
                <b-form-input
                    id="interval-input"
                    ref="templateIntervalInput"
                    v-model="templateModal.interval"
                    min="1"
                    type="number">
                </b-form-input>
                <b-tooltip target="interval-input-group" triggers="hover">
                  Number of days to filter and aggregate in this template's moving date window. Based on the selected
                  start and end dates.
                </b-tooltip>
              </b-input-group>

              <b-form-group class="mt-2" label="Dimensions">
                <b-form-tags
                    id="template-dimensions"
                    :value="checkedCols"
                    disabled
                    placeholder=""
                    tag-variant="primary">
                </b-form-tags>
              </b-form-group>

              <b-form-group class="mt-2" label="Metrics">
                <b-form-tags
                    id="template-dimensions"
                    :value="checkedMetrics"
                    disabled
                    placeholder=""
                    tag-variant="primary">
                </b-form-tags>
              </b-form-group>

              <b-form-group class="mt-2" label="Filters">
                <ul id="template-filter" style="list-style-type: none">
                  <div v-for="(g) in filterGroups">
                    <div v-if="g.expressions.length > 0">
                      <label>{{ g.name }}</label>
                      <li v-for="(f, index) in g.expressions" :key="index">
                        <i>{{ f.join }}</i> <b>{{ f.obj }}</b> {{ f.op }}
                        <b>{{ f.val }}</b>
                      </li>
                    </div>
                    <br/>
                  </div>
                </ul>
              </b-form-group>

              <b-form-group class="mt-2" label="Email Notification">
                <b-form-checkbox
                    id="template-email-checkbox"
                    ref="templateAlert"
                    v-model="templateModal.alert"
                    switch
                    type="checkbox">
                </b-form-checkbox>
              </b-form-group>

              <div v-if="this.templateModal.alert">
                <b-input-group class="mt-2" prepend="Schedule">
                  <b-form-select
                      id="template-schedule-select"
                      ref="templateSchedule"
                      v-model="templateModal.schedule"
                      value="daily">
                    <b-form-select-option selected value="daily">
                      Daily
                    </b-form-select-option>
                    <b-form-select-option value="weekly">
                      Weekly
                    </b-form-select-option>
                  </b-form-select>
                </b-input-group>

                <b-form-group class="mt-2" label="Send notification on empty">
                  <b-form-checkbox
                      id="template-empty-notification-checkbox"
                      ref="templateEmptyAlert"
                      v-model="templateModal.alertOnEmpty"
                      switch
                      type="checkbox">
                  </b-form-checkbox>
                </b-form-group>

                <b-form-group class="mt-2" label="Send report to emails">
                  <b-form-textarea
                      id="template-emails"
                      ref="templateEmails"
                      v-model="templateModal.emails"
                      class="form-control"
                      rows="3">
                  </b-form-textarea>
                </b-form-group>
              </div>

              <div v-if="errors.template.length" class="mt-2 alert-danger">
                <b>Please correct the following error(s):</b>
                <ul>
                  <li v-for="error in errors">{{ error }}</li>
                </ul>
              </div>

              <div class="mt-2 mb-1">
                <b-button
                    style="float: left"
                    variant="danger"
                    @click="hideTemplateModal">
                  Close
                </b-button>
                <b-button style="float: right" type="submit" variant="primary">
                  Save
                </b-button>
              </div>
            </b-form>
          </b-modal>
        </b-col>
        <b-col v-if="context === 'audience'">
          <div>
            <label class="bold">Linked Audience Lists</label>
            <b-form-select
                id="al-select"
                v-model="selectedAL"
                @change="changeAL()">
              <b-form-select-option
                  v-for="al in userALs"
                  :key="al.id + al.email"
                  :value="al">
                {{ al.id }} - {{ al.email }}
              </b-form-select-option>
            </b-form-select>
          </div>

          <div class="mt-2">
            <b-button variant="outline-primary" @click="showALModal">Link</b-button>
            <b-button
                :disabled="alDeleteDisabled"
                variant="outline-danger"
                @click="deleteAL">Unlink
            </b-button>
          </div>

          <b-modal
              id="al-modal"
              ref="alModal"
              centered
              hide-footer
              title="Link Audience List"
              @bv::modal::hidden="this.errors.al = []">
            <b-form id="al-form" @submit.prevent="submitAlForm">
              <b-input-group class="mt-2" prepend="Link with">
                <b-form-select
                  id="al-link-select"
                  v-model="linkedAL">
                  <b-form-select-option
                      v-for="al in als"
                      :key="al.id"
                      :value="al">
                    {{ al.id }} | {{ al.name }} | {{ al.company_name }}
                  </b-form-select-option>
                </b-form-select>
              </b-input-group>
              <div v-if="linkedAL">
                <label class="mt-3"><b><i>Audience List attributes</i></b></label>
                <br/>
                <b-row>
                  <b-col>
                    <label><b>advertiser: </b>{{ linkedAL.company_name }}</label>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <label><b>id: </b>{{ linkedAL.id }}</label>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <label><b>name: </b>{{ linkedAL.name }}</label>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <label><b>duration: </b>{{ linkedAL.duration }}</label>
                  </b-col>
                </b-row>
              </div>
              <b-form-group class="mt-2">
                <label class="mt-3"><b><i>Filters</i></b></label>
                <ul id="template-filter" style="list-style-type: none">
                  <div v-for="(g) in filterGroups">
                    <div v-if="g.expressions.length > 0">
                      <label>{{ g.name }}</label>
                      <li v-for="(f, index) in g.expressions" :key="index">
                        <i>{{ f.join }}</i> <b>{{ f.obj }}</b> {{ f.op }}
                        <b>{{ f.val }}</b>
                      </li>
                    </div>
                    <br/>
                  </div>
                </ul>
              </b-form-group>

              <div class="mt-2 mb-1">
                <b-button
                    style="float: left"
                    variant="danger"
                    @click="hideALModal">
                  Close
                </b-button>
                <b-button style="float: right" type="submit" variant="primary">
                  Link
                </b-button>
              </div>
            </b-form>
          </b-modal>
        </b-col>
        <b-col>
          <label class="bold" for="start-datepicker">Choose start date</label>
          <b-form-datepicker
              id="start-datepicker"
              v-model="startDate"
              @input="enableChartUpdate">
          </b-form-datepicker>
          <label class="bold mt-1" for="end-datepicker">Choose end date</label>
          <b-form-datepicker
              id="end-datepicker"
              v-model="endDate"
              @input="enableChartUpdate">
          </b-form-datepicker>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <label class="bold" for="select-dimensions">Select dimensions</label>
        </b-col>
        <b-col class="text-right">
          <b-link @click="selectAllDims">all</b-link>
          <label>|</label>
          <b-link @click="selectNoDims">none</b-link>
          <label>|</label>
          <b-link @click="resetDims">reset</b-link>
        </b-col>
      </b-row>

      <b-row class="mx-2">
        <b-form-checkbox-group
            v-model="checkedCols"
            stacked
            style="column-count: 4"
            switches>
          <b-form-checkbox
              v-for="dim in dimensions"
              :key="dim.name"
              :value="dim.value"
              class="mr-1">
            {{ dim.name }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <label class="bold" for="select-metrics">Select metrics</label>
        </b-col>
        <b-col class="text-right">
          <b-link @click="selectAllMetrics">all</b-link>
          <label>|</label>
          <b-link @click="selectNoMetrics">none</b-link>
          <label>|</label>
          <b-link @click="resetMetrics">reset</b-link>
        </b-col>
      </b-row>

      <b-row class="mx-2">
        <b-form-checkbox-group
            v-model="checkedMetrics"
            stacked
            style="column-count: 4"
            switches>
          <b-form-checkbox
              v-for="metric in metrics"
              :key="metric.name"
              :value="metric.value"
              class="mr-1">
            {{ metric.name }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <label class="bold" for="select-metrics">Filtering</label>
        </b-col>
      </b-row>
      


      <div class="mb-3" v-for="(filterGroup) in filterGroups">
        <b-card header-tag="header">
          <template #header>
            <h6 class="mb-0">{{ filterGroup.name }} Filters</h6>
          </template>
          <b-row>
            <b-col>
              <b-form-group>
                <b-input-group size="md">
                  <b-input-group-prepend>
                    <b-form-radio-group
                        v-model="filterGroup.filterJoin"
                        :disabled="filterGroup.expressions.length === 0"
                        :options="filterJoinOptions"
                        button-variant="outline-primary"
                        buttons>
                    </b-form-radio-group>
                  </b-input-group-prepend>
                  <b-form-select
                      v-model="filterGroup.filterOn"
                      :options="filterGroup.filterKeys"
                      text-field="name"
                      value-field="value">
                  </b-form-select>
                  <b-form-select
                      v-model="filterGroup.filterOp"
                      :options="filterOps"
                      text-field="name"
                      value-field="value">
                  </b-form-select>
                  <b-form-input
                      id="filter-input"
                      v-model="filterGroup.filterValue"
                      placeholder="Type filter value"
                      type="search">
                  </b-form-input>
                  <b-input-group-append>
                    <b-button
                        :disabled="!filterGroup.filterValue"
                        variant="outline-primary"
                        @click="addFilter(filterGroup)">
                      +
                    </b-button>
                    <b-button
                        class="ml-3"
                        variant="outline-danger"
                        @click="clearFilterGroup(filterGroup)">
                      Clear
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
          <div v-if="filterGroup.expressions.length > 0">
            <b-row>
              <b-col>
                <label>Active Filters:</label>
              </b-col>
            </b-row>
            <ul id="filter-lists" style="list-style-type: none; padding-left: 0;">
              <div>
                <li v-for="(f, index) in filterGroup.expressions" :key="index">
                  <b-form-group>
                    <b-input-group size="md">
                      <b-input-group-prepend>
                        <b-form-radio-group
                            v-model="f.join"
                            :disabled="f.join.length === 0 || index === 0"
                            :options="filterJoinOptions"
                            button-variant="outline-primary"
                            buttons>
                        </b-form-radio-group>
                      </b-input-group-prepend>
                      <b-form-select
                          v-model="f.obj"
                          :options="filterGroup.filterKeys"
                          text-field="name"
                          value-field="value">
                      </b-form-select>
                      <b-form-select
                          v-model="f.op"
                          :options="filterOps"
                          text-field="name"
                          value-field="value">
                      </b-form-select>
                      <b-form-input
                          id="filter-input"
                          v-model="f.val"
                          placeholder="Type filter value"
                          type="search">
                      </b-form-input>
                      <b-form-checkbox 
                        v-if="filterGroup.name === 'Metrics'" 
                        v-model="f.grouped" 
                        name="grouped-button" 
                        button
                        button-variant="outline-primary">
                        Grouped
                      </b-form-checkbox>
                      <b-input-group-append>
                        <b-button
                          variant="outline-danger"
                          @click="clearFilterIndex(filterGroup, index)">
                          <b-icon
                            icon="x-circle"
                            scale="0.9">
                          </b-icon>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </li>
              </div>
            </ul>
          </div>
        </b-card>
      </div>

      <b-row class="mt-4">
        <b-col/>
        <b-col>
          <b-button block variant="primary" @click="fetchData">
            Show Data
          </b-button>
        </b-col>
        <b-col/>
      </b-row>
      <b-row class="my-3" v-if="!hideChart">
        <MetricsChart
            ref = "chart"
            :context="context"
            :endDate="endDate"
            :filtersQuery="filtersQuery"
            :metrics="metrics"
            :plotMetrics="plotMetrics"
            :startDate="startDate"
        />
      </b-row>

      <b-row class="mt-3">
        <b-col class="text-right">
          <b-button size="sm" variant="primary" @click="downloadCsv">
            <b-icon v-if="!csvDownloading" icon="cloud-download"></b-icon>
            <b-spinner
                v-if="csvDownloading"
                class="mx-center"
                label="Downloading..."
                small
                variant="outline-primary">
            </b-spinner>
            CSV
          </b-button>
        </b-col>
      </b-row>
      <b-row align-h="center" class="mt-1">
        <!-- Main table element -->
        <div v-if="loading" class="text-center">
          <b-spinner
              class="mx-center"
              label="Loading..."
              variant="secondary"
          ></b-spinner>
        </div>
        <div v-if="success" class="mx-3 table table-hover">
          <b-table
              ref="table"
              :fields="fields"
              :fixed="this.fixedColumns"
              :items="formattedItems"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              bordered
              hover
              responsive="md"
              selectable
              selected-variant="primary"
              @row-clicked="onRowClicked"
              show-empty
              small
              striped
              @sort-changed="sortChanged">
            <template #cell(name)="row">
              {{ row.value.first }} {{ row.value.last }}
            </template>
          </b-table>
        </div>
      </b-row>

      <b-row align="center">
        <b-col>
          <b-form-group
              class="mb-0"
              label="Per page"
              label-align-sm="right"
              label-cols-lg="3"
              label-cols-md="4"
              label-cols-sm="6"
              label-for="per-page-select"
              label-size="md">
            <b-form-select
                id="per-page-select"
                v-model="perPage"
                :options="pageOptions"
                size="md"
                @change="handlePageSizeChange">
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col>
          <b-button-group>
            <b-button
                :disabled="currentPage == 1"
                variant="primary"
                v-on:click="updateToFirstPage">
              Go To First
            </b-button>
            <b-button
                :disabled="currentPage == 1"
                variant="primary"
                v-on:click="updateToPreviousPage">
              Previous
            </b-button>
            <b-button disabled variant="outline-primary">{{ currentPage }}</b-button>
            <b-button variant="primary" v-on:click="updateToNextPage">Next</b-button>
          </b-button-group>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import MetricsChart from "@/components/MetricsChart";
import util from "../util/util"
import defaults from "../util/defaults"

export default {
  name: "DataView",
  components: {MetricsChart},
  props: [
    "context",
    "defaultCheckedCols",
    "defaultCheckedMetrics",
    "fixedColumns",
    "plotMetrics",
    "initialTemplate",
    "defaultSort",
    "back"
  ],
  data() {
    return {
      loading: true,
      success: false,
      csvDownloading: false,
      updateChart: false,
      hideChart: false,
      dimensions: [],
      metrics: [],
      templates: [],
      checkedCols: this.defaultCheckedCols,
      checkedMetrics: this.defaultCheckedMetrics,
      items: [],
      fields: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 25,
      pageOptions: [25, 50, 100],
      filterKeys: [],
      filterOps: [],
      startDate: "",
      endDate: "",
      sortBy: this.defaultSort,
      sortDesc: true,
      chartData: null,
      filterGroups: [],
      filters: [],
      filtersQuery: [],
      filterJoinOptions: ["AND", "OR"],
      filterHover: -1,
      errors: {
        template: [],
        al: [],
      },
      templateModal: {
        name: "",
        interval: "",
        alert: false,
        alertOnEmpty: false,
        scheduleOptions: [
          {value: "daily", text: "Daily"},
          {value: "weekly", text: "Weekly"},
        ],
        schedule: "daily",
        emails: "",
      },
      selectedTemplate: null,
      templateDeleteDisabled: true,
      userALs: [],
      als: [],
      alModal: {
        name: "",
      },
      selectedAL: null,
      linkedAL: null,
      alDeleteDisabled: true,
      loggedUserRole: null,
    };
  },
  computed: {
    getCheckedColsString() {
      return this.checkedCols.join(",");
    },
    getCheckedMetricsString() {
      return this.checkedMetrics.join(",");
    },
    formattedItems() {
      if (!this.items) return [];
      return this.items.map(item => {
        let variant = defaults.getVariant(item.Qualifier);
        if (variant !== "default")
          item._rowVariant = variant;
        else
          item._rowVariant = null;
        return item;
      })
    },
  },
  methods: {
    onRowClicked(item) {
      if (item._rowVariant) {
        if (item._rowVariant !== "primary")
          item._rowVariant = "primary";
        else
          item._rowVariant = defaults.getVariant(item.Qualifier);
      }
    },
    isAdminView() {
      return (
          firebase.auth().currentUser.displayName === "admin" &&
          this.$store.getters.demoUid === ""
      );
    },
    enableChartUpdate() {
      this.updateChart = true;
    },
    getKeyByValue(list, value) {
      return list.find((element) => element.value === value).name;
    },
    getValueByKey(list, key) {
      return list.find((element) => element.name === key).value;
    },
    buildFilterExpressionQuery(expression) {
      return (expression.join === "" ? "" : expression.join + "_") +
      expression.obj +
      "_" +
      expression.op +
      "_" +
      expression.val +
      (expression.grouped ? "_G" : "");
    },
    buildFilterQuery() {
      this.filtersQuery = [];
      this.filterGroups.forEach((group) => {
        group.expressions.forEach((f) => 
          this.filtersQuery.push(this.buildFilterExpressionQuery(f)));
      });
    },
    addFilter(filterGroup) {
      if (filterGroup.filterOn !== "" && filterGroup.filterOp !== "" && filterGroup.filterValue !== "") {
        filterGroup.expressions.push({
          join: filterGroup.expressions.length === 0 ? "" : filterGroup.filterJoin,
          obj: filterGroup.filterOn,
          op: filterGroup.filterOp,
          val: filterGroup.filterValue,
        });
        filterGroup.filterJoin = "AND";
        filterGroup.filterValue = "";
        filterGroup.filterOn = "";
        filterGroup.filterOp = "";
        this.enableChartUpdate();
      }
    },
    clearFilterIndex(filterGroup, index) {
      if (index > -1) {
        filterGroup.expressions.splice(index, 1);
        if (filterGroup.expressions.length === 0) {
          this.clearFilterGroup(filterGroup);
          return;
        }
      }
    },
    clearFilterGroup(filterGroup) {
      filterGroup.expressions = [];
      filterGroup.filterValue = "";
      filterGroup.filterOn = "";
      filterGroup.filterOp = "";
      this.enableChartUpdate();
    },
    clearFilter() {
      this.filterGroups.forEach((group) => this.clearFilterGroup(group));
    },
    sortChanged(state) {
      this.sortBy = state.sortBy;
      this.sortDesc = state.sortDesc;
      this.currentPage = 1;
      this.fetchData("table");
    },
    updateToFirstPage() {
      this.currentPage = 1;
      this.fetchData("table");
    },
    updateToPreviousPage() {
      this.currentPage = this.currentPage - 1;
      this.fetchData("table");
    },
    updateToNextPage() {
      this.currentPage = this.currentPage + 1;
      this.fetchData("table");
    },
    handlePageSizeChange(value) {
      this.perPage = value;
      this.fetchData("table");
    },
    selectAllMetrics() {
      this.checkedMetrics = [];
      this.metrics.forEach((element) => {
        this.checkedMetrics.push(element.value);
      });
    },
    selectNoMetrics() {
      this.checkedMetrics = [];
    },
    resetMetrics() {
      this.checkedMetrics = this.defaultCheckedMetrics;
    },
    selectAllDims() {
      this.checkedCols = [];
      this.dimensions.forEach((element) => {
        this.checkedCols.push(element.value);
      });
    },
    selectNoDims() {
      this.checkedCols = [];
    },
    resetDims() {
      this.checkedCols = this.defaultCheckedCols;
    },
    updateFields(sourceArr, checkedArr) {
      sourceArr.forEach((element) => {
        if (checkedArr.includes(element.value)) {
          this.fields.push({
            key: element.value,
            label: element.value,
            sortable: true,
            sortDirection: "desc",
          });
        }
      });
    },
    async fetchData(ctx) {
      this.filterGroups.forEach((filterGroup) => this.addFilter(filterGroup));
      this.buildFilterQuery();
      this.loading = true;
      this.success = false;

      if (this.isServiceTemplate()) {
        this.hideChart = true;
      }

      // handles cases when the sorting is set for non-checked dimensions or metrics
      if (this.dimensions.some(elem => elem.name === this.sortBy) && !this.checkedCols.includes(this.sortBy)) {
        this.sortBy = this.defaultSort;
        this.sortDesc = true;
      }
      if (this.metrics.some(elem => elem.name === this.sortBy) && !this.checkedMetrics.includes(this.sortBy) && this.checkedMetrics.length > 0) {
        this.sortBy = this.checkedMetrics[0];
        this.sortDesc = true;
      }

      try {
        let paramMap = defaults.getParamMap.call(this)
            .set(defaults.PARAMS.pageNum, this.currentPage).set(defaults.PARAMS.pageSize, this.perPage)
            .set(defaults.PARAMS.serviceTemplate, this.isServiceTemplate() ? this.selectedTemplate.name : "")

        this.items = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.query, paramMap)
            .then((response) => response.json());

        this.totalRows = this.items.length;
        this.fields = [];
        
        this.updateFields(this.dimensions, this.checkedCols)
        this.updateFields(this.metrics, this.checkedMetrics)

        this. success = true
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
      if (!this.hideChart && ctx !== "table" && (this.updateChart || util.doDatesIncludeToday(this.startDate, this.endDate))) {
        this.$refs.chart.fetchData()
        this.updateChart = false;
      }
    },
    async downloadCsv() {
      this.csvDownloading = true;
      this.filterGroups.forEach((filterGroup) => this.addFilter(filterGroup));

      try {
        let paramMap = defaults.getParamMap.call(this)
            .set(defaults.PARAMS.metrics,  this.checkedMetrics.join(","))

        let csv = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.csv, paramMap)
          .then((response) => response.blob());

        util.downloadFile(`seetrue.io-${this.context}-data-export-${this.start_date}-${this.end_date}-UTC.csv"`, csv);
      } catch (error) {
        console.log(error);
      } finally {
        this.csvDownloading = false;
      }
    },
    initFilterStructure() {
      this.filterGroups = [
        {
          name: "Dimensions", 
          expressions: [], 
          filterKeys: [{name: "Select what to filter", value: ""}, ...this.dimensions],
          filterJoin: "AND",
          filterValue: "",
          filterOn: "",
          filterOp: "",
        },
        {
          name: "Metrics", 
          expressions: [], 
          filterKeys: [{name: "Select what to filter", value: ""}, ...this.metrics],
          filterJoin: "AND",
          filterValue: "",
          filterOn: "",
          filterOp: "",
        },
      ];
    },
    async fetchSchema() {
      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context, defaults.PARAMS.demoUid]);

        let jsonData = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.schema, paramMap)
        .then((response) => response.json()).catch(error => console.log(error));

        this.dimensions = jsonData.dimensions;
        this.metrics = jsonData.metrics;

        this.initFilterStructure();

        let supportedOps = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.dbOps)
            .then((response) => response.json()).catch(error => console.log(error));
        
        this.filterKeys = [{name: "Select what to filter", value: ""}, ...this.dimensions, ...this.metrics]
        this.filterOps = [{name: "Select filter operation", value: ""}, ...supportedOps];

    },
    updateDates(interval, daysBack) {
      [this.startDate, this.endDate] = util.getUpdatedDates(interval, daysBack)
    },
    async submitTemplateForm() {
      this.buildFilterQuery();

      const templateToSend = {
        name: this.templateModal.name,
        templateType: this.context,
        interval: parseInt(this.templateModal.interval) - 1,
        dimensions: this.getCheckedColsString,
        metrics: this.getCheckedMetricsString,
        filters: this.filtersQuery.join(";"),
        alert: +this.templateModal.alert,
        emptyAlert: +this.templateModal.alertOnEmpty,
        schedule: this.templateModal.schedule,
        emails: this.templateModal.emails.replace(/\s/g, ''),
      };

      if (!this.validateTemplate(templateToSend)) {
        return;
      }

      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.demoUid])
      let dataJson = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.templates, paramMap, "POST", templateToSend)
          .then((response) => {
            if (!response.ok) {
              throw Error(response.body);
            }
            return response.text();
          })
          .catch((error) => {
            this.errors.template.push(error.message);
            console.error("Error:", error);
          });

      if (this.errors.template.length === 0) {
        this.setupTemplates(templateToSend.name);
        this.hideTemplateModal();
      }
    },
    validateTemplate(template) {
      this.errors.template = [];

      if (template.name === "") {
        this.errors.template.push("Template name should not be empty");
      }

      if (this.templates.map((t) => t.name).includes(template.name)) {
        this.errors.template.push("Template name already present");
      }

      if (!defaults.emailListRegex.test(template.emails)) {
        this.errors.template.push(
            "E-mail list should contain valid e-mail addresses separated with ','"
        );
      }

      return this.errors.template.length === 0;
    },
    hideTemplateModal() {
      this.$refs.templateModal.hide();
    },
    showTemplateModal() {
      this.templateModal.name = "";
      this.templateModal.interval = util.getDayInterval(this.startDate, this.endDate);
      this.templateModal.emails = firebase.auth().currentUser.email;
      this.$refs.templateModal.show();
    },
    async setupTemplates(selectedTemplateName) {
      this.templates = [];
      let [defaultTemplate, wlTemplate, blTemplate, explrTemplate] = defaults.getDefaultTemplates.call(this)

      this.templates.push(defaultTemplate);
      this.templates.push(wlTemplate);
      this.templates.push(blTemplate);
      this.templates.push(explrTemplate);

      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context,defaults.PARAMS.demoUid])
      let dataJson = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.templates, paramMap)
          .then((response) => response.json())
          .catch((error) => {
            console.error("Error:", error);
          });

      if (dataJson.length > 0) {
        this.templates = this.templates.concat(dataJson);
      }

      if (selectedTemplateName) {
        this.selectedTemplate = this.templates.find((t) => {
        return t.name === selectedTemplateName;
      });
      } else {
        let template = "Default";
        if (this.templates.some((e) => e.name === this.initialTemplate)) {
          template = this.initialTemplate;
        }
        this.selectedTemplate = this.templates.find((t) => {
        return t.name === template;
      });
      }

      this.changeTemplate();
    },
    isServiceTemplate() {
      return this.selectedTemplate.name === "whitelist" || this.selectedTemplate.name === "blacklist" || this.selectedTemplate.name === "exploration";
    },
    changeTemplate() {
      if (this.isServiceTemplate()) {
        this.checkedCols = ["Placement Id", "AdNetwork", "Offer Thematic", "AdFormat", "Is Mobile"];
        this.clearFilter();
        return;
      }

      this.hideChart = false;
      this.updateDates(this.selectedTemplate.interval, this.back);

      this.checkedCols = this.selectedTemplate.dimensions;
      this.checkedMetrics = this.selectedTemplate.metrics;
      this.clearFilter();

      this.selectedTemplate.dimensionFilters.forEach((f) => {
        let parts = f.split("_");
        if (parts.length === 3) {
          parts.unshift("");
        }

        this.filterGroups.find((t) => {
          return t.name === "Dimensions";
        }).expressions.push({
          join: parts[0],
          obj: parts[1],
          op: parts[2],
          val: parts[3],
        });
      });

      this.selectedTemplate.metricFilters.forEach((f) => {
        let parts = f.split("_");
        if (parts.length === 3) {
          parts.unshift("");
        }

        this.filterGroups.find((t) => {
          return t.name === "Metrics";
        }).expressions.push({
          join: parts[0],
          obj: parts[1],
          op: parts[2],
          val: parts[3],
        });
      });
    },
    async deleteTemplate() {
      if (this.selectedTemplate.name === "Default") {
        alert("Default template cannot be deleted");
        return;
      }
      if (!confirm("Delete template " + this.selectedTemplate.name + "?")) {
        return;
      }

      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context, defaults.PARAMS.demoUid])
          .set(defaults.PARAMS.name, this.selectedTemplate.name)

      let jsonData = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.templates, paramMap, "DELETE")
          .then((res) => res.text())
          .catch((err) => console.log(err));

      this.setupTemplates();
      this.templateDeleteDisabled = true;
    },
    async fetchAndSetupInitialData() {
      this.updateDates(7, 0);
      await this.setupTemplates();
      await this.fetchUserALs();
      await this.fetchData();
    },
    hideALModal() {
      this.$refs.alModal.hide();
    },
    async showALModal() {
      this.als = [];
      await this.fetchALs();
      this.$refs.alModal.show();
    },
    async submitAlForm() {
      this.errors.al = [];
      this.buildFilterQuery();

      const alToSend = {
        id: this.linkedAL.id,
        filters: this.filtersQuery.join(";")
      };

      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.demoUid])
      let dataJson = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.uals, paramMap, "POST", alToSend)
          .then((response) => {
            if (!response.ok) {
              throw Error(response.body);
            }
            return response.text();
          })
          .catch((error) => {
            this.errors.al.push(error.message);
            console.error("Error:", error);
          });

      if (this.errors.al.length === 0) {
        await this.fetchUserALs();
        this.selectedAL = this.userALs.find((el) => el.id === alToSend.id && el.email === "own");
        this.hideALModal();
      }
    },
    async fetchALs() {
      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context,defaults.PARAMS.demoUid])
      let dataJson = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.als, paramMap)
          .then((response) => response.json())
          .catch((error) => {
            console.error("Error:", error);
          });
      if (dataJson.length > 0) {
        this.als = dataJson;
      }
    },
    async fetchUserALs() {
      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context,defaults.PARAMS.demoUid])
      let dataJson = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.uals, paramMap)
          .then((response) => response.json())
          .catch((error) => {
            console.error("Error:", error);
          });
      if (dataJson.length > 0) {
        this.userALs = dataJson;
      } else {
        this.userALs = [];
      }
    },
    changeAL() {
      this.hideChart = false;
      this.clearFilter();
      this.selectedAL.dimensionFilters.forEach((f) => {
        let parts = f.split("_");
        if (parts.length === 3) {
          parts.unshift("");
        }

        this.filterGroups.find((t) => {
          return t.name === "Dimensions";
        }).expressions.push({
          join: parts[0],
          obj: parts[1],
          op: parts[2],
          val: parts[3],
        });
      });

      this.selectedAL.metricFilters.forEach((f) => {
        let parts = f.split("_");
        if (parts.length === 3) {
          parts.unshift("");
        }

        this.filterGroups.find((t) => {
          return t.name === "Metrics";
        }).expressions.push({
          join: parts[0],
          obj: parts[1],
          op: parts[2],
          val: parts[3],
        });
      });
    },
    async deleteAL() {
      if (this.selectedAL === null) {
        return;
      }
      if (!confirm("Delete audience list link " + this.selectedAL.id + "?")) {
        return;
      }

      let paramMap = defaults.getParamMap.call(this, [defaults.PARAMS.context, defaults.PARAMS.demoUid])
          .set(defaults.PARAMS.name, this.selectedAL.id)

      let jsonData = await util.apiCall(process.env.VUE_APP_BASE_URL, defaults.ROUTES.uals, paramMap, "DELETE")
          .then((res) => res.text())
          .catch((err) => console.log(err));

      await this.fetchUserALs();
      this.selectedAL = null;
      this.alDeleteDisabled = true;
    },
  },
  watch: {
    selectedTemplate(newValue, oldValue) {
      this.templateDeleteDisabled = newValue.name === "Default";
    },
    selectedAL(newValue, oldValue) {
      this.alDeleteDisabled = this.selectedAL === null || this.selectedAL.email !== "own";
    },
  },
  async created() {
    this.vuexUnsubscribe = this.$store.subscribe(async (mutation, state) => {
      if (mutation.type === "setDemoUid") {
        this.updateChart = true;
        this.sortBy = "Total Visits";
        this.sortDesc = true;
        this.templates = [];
        await this.fetchSchema();
        await this.fetchAndSetupInitialData();
      }
    });

    this.loggedUserRole = firebase.auth().currentUser.displayName;

    await this.fetchSchema();
    await this.fetchAndSetupInitialData();
  },
  beforeDestroy() {
    this.vuexUnsubscribe();
  },
};
</script>
<style>
.table {
  font-size: 12px;
  overflow-x: scroll;
}

.bold {
  font-weight: bold;
}

.btn:focus {
  outline: none;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// src/components/DataView.vue